import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, addDays, isBefore, isAfter } from 'date-fns';

export interface LeagueGameDate {
  date: string;           // "2026-01-30T06:00:00.000Z"
  formattedDate: string;  // "Vie 30 ene"
  dayName: string;        // "Viernes"
  phase: string;          // "grupos"
}

export interface EliminationPhaseSchedule {
  date: string;
  time?: string;
  startTime?: string;
  endTime?: string;
}

interface ScheduleData {
  gameDates?: LeagueGameDate[];
  totalGameDates?: number;
  leagueFormat?: string;
  eliminationPhases?: Record<string, EliminationPhaseSchedule>;
  [key: string]: any;
}

/**
 * Hook to fetch game dates directly from tournament_schedules table.
 * This ensures the calendar reflects any updates made by the organizer,
 * rather than calculating from the static total_weeks in the events table.
 */
export function useLeagueSchedule(eventId: string) {
  const [gameDates, setGameDates] = useState<LeagueGameDate[]>([]);
  const [totalGameDates, setTotalGameDates] = useState<number>(0);
  const [leagueFormat, setLeagueFormat] = useState<string>('');
  const [eliminationPhases, setEliminationPhases] = useState<Record<string, EliminationPhaseSchedule>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchSchedule() {
      if (!eventId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('tournament_schedules')
          .select('schedule_data')
          .eq('tournament_id', eventId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching league schedule:', error);
          setLoading(false);
          return;
        }

        if (data?.schedule_data) {
          const scheduleData = data.schedule_data as ScheduleData;
          
          if (scheduleData.gameDates && Array.isArray(scheduleData.gameDates)) {
            setGameDates(scheduleData.gameDates);
          }
          
          if (typeof scheduleData.totalGameDates === 'number') {
            setTotalGameDates(scheduleData.totalGameDates);
          }
          
          if (scheduleData.leagueFormat) {
            setLeagueFormat(scheduleData.leagueFormat);
          }
          
          // Extract elimination phase schedules if present
          if (scheduleData.eliminationPhases && typeof scheduleData.eliminationPhases === 'object') {
            setEliminationPhases(scheduleData.eliminationPhases);
          }
        }
      } catch (err) {
        console.error('Exception fetching league schedule:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchSchedule();
  }, [eventId]);

  return { gameDates, totalGameDates, leagueFormat, eliminationPhases, loading };
}

/**
 * Calculates the status of a game date relative to today.
 */
export function calculateGameDateStatus(dateStr: string): 'past' | 'current' | 'future' {
  const today = startOfDay(new Date());
  const gameDate = startOfDay(new Date(dateStr));

  if (isNaN(gameDate.getTime())) {
    return 'future';
  }

  // Use 7-day range: [gameDate, gameDate+6]
  const gameEnd = startOfDay(addDays(gameDate, 6));

  if (isAfter(today, gameEnd)) {
    return 'past';
  } else if (!isBefore(today, gameDate) && !isAfter(today, gameEnd)) {
    return 'current';
  } else {
    return 'future';
  }
}

/**
 * Extracts date portion from formatted date string.
 * E.g., "Vie 30 ene" -> "30 ene"
 */
export function extractDateFromFormatted(formattedDate: string): string {
  const parts = formattedDate.split(' ');
  if (parts.length >= 3) {
    return parts.slice(1).join(' '); // Remove day name, keep "30 ene"
  }
  return formattedDate;
}
